import React from 'react';
import Widget from './widget';
import { defaultConfiguration, parsePositiveIntId, colorToRgbTriplet, resolveInlineMinHeightFallback } from './default-configuration';
import ApiService from '../services/api';
import { setSysThemeHint, structureTokensCssText, ensureStructureTokenStyle } from './theme-color';
import nativeBridge, { resolveNavigateTarget, navigateTargetFromDeepLink } from './native-bridge.js';
import { debugLog } from '../debug';
// import {createRoot} from "react-dom/client";

// SDK Bridge Protocol：widget 自身版本号，随 hello 一起发给 native 容器。
// 没有既有版本常量可复用，独立维护；与 npm 包版本无强绑定关系。
var WIDGET_VERSION = '0.2.0';

// import '../../css/chat.css'; // 引入普通的CSS文件

/**
 * ============================================================================
 * Lane.Chat Widget SDK 接入指南
 * ============================================================================
 *
 * 基本用法（自动初始化）：
 *   <script src="/widget.js" data-app-key="YOUR_APP_KEY"></script>
 *
 * 手动初始化（SPA / 路由切换场景）：
 *   window.laneChatManualInit = true;
 *   window.laneChatAppId = 'YOUR_APP_KEY';
 *   window.laneChatSDK.init();
 *
 * SPA 路由切换时建议流程：
 *   // 离开页面时
 *   window.laneChatSDK.destroy();
 *   // 进入新页面后
 *   window.laneChatSDK.init();
 *
 * 可选 postMessage 源白名单（主页面监听 iframe 消息时使用）：
 *   window.laneChatAllowedOrigins = [
 *     'https://your-iframe-host.example.com',
 *     'https://webmanager.lane.chat'
 *   ];
 *   空则默认只允许同源 + 内置服务端 origin。
 *
 * 安全说明：
 *   - 所有 postMessage 调用都指定具体 target origin，不再使用 '*'。
 *   - 所有 message 监听器都校验 event.origin 白名单。
 *   - injectChat() 幂等：重复调用不会产生多个 widget 实例。
 * ============================================================================
 */

// 构建允许的父窗口 / iframe 宿主 origin 列表
// - 主窗口监听 iframe 消息时做白名单校验
// - 暴露为 window.__laneChatGetAllowedOrigins 以便第三方扩展或调试
function getAllowedOrigins() {
    const extras = Array.isArray(window.laneChatAllowedOrigins)
        ? window.laneChatAllowedOrigins
        : [];
    const server = window.laneChatServerUrl;
    const base = [window.location.origin];
    if (server) {
        try {
            base.push(new URL(server).origin);
        } catch (e) {
            // ignore invalid server url
        }
    }
    return Array.from(new Set([...base, ...extras].filter(Boolean)));
}
if (typeof window !== 'undefined') {
    window.__laneChatGetAllowedOrigins = getAllowedOrigins;
}

// 创建聊天界面的HTML内容
function createChatHtmlContent() {
    return `<!doctype html>
<html>
<head>
    <meta charset="UTF-8">
    <title>Chat</title>
    <style>
        /* 基础样式 */
        html, body {
            background: #f8fafc;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            margin: 0;
            padding: 0;
            height: 100%;
            overflow: hidden;
        }

        #laneChatWidget {
            width: 100%;
            height: 100%;
            position: relative;
        }

        /* 聊天界面样式 */
        .chat-container {
            height: 100%;
            width: 100%;
            position: relative;
            background: #f8fafc;
            display: flex;
            flex-direction: column;
        }

        .loading {
            display: flex;
            justify-content: center;
            align-items: center;
            height: 100%;
            font-size: 14px;
            color: #666;
        }
    </style>
</head>
<body>
    <div id="laneChatWidget">
        <div class="chat-container">
            <div class="loading">正在加载聊天界面...</div>
        </div>
    </div>

    <script>
        // 动态获取允许的origin - 支持开发和生产环境
        const allowedOrigins = [
            window.location.origin,
            "http://localhost:3000",
            "http://localhost:8787",
            "https://push.chatly.cc",
            "https://webmanager.lane.chat"
        ];

        // 从 document.referrer 推导宿主页 origin（iframe 内）
        function resolveParentOrigin() {
            try {
                if (document.referrer) {
                    var refOrigin = new URL(document.referrer).origin;
                    if (allowedOrigins.indexOf(refOrigin) !== -1) return refOrigin;
                    return refOrigin;
                }
            } catch (e) { /* ignore */ }
            // 最小安全默认值：当前窗口自身 origin
            return window.location.origin;
        }

        window.addEventListener("message", (event) => {
            if (allowedOrigins.indexOf(event.origin) === -1) return;
            if (event.data && event.data.type === 'scrollToBottom') {
                window.scrollTo(0, document.body.scrollHeight);
            } else if (event.data && event.data.type === 'updateContent') {
                // 处理内容更新
                if (event.data.content) {
                    document.getElementById('laneChatWidget').innerHTML = event.data.content;
                }
            }
        });

        // 通知父窗口iframe已加载（指定具体 target origin，避免泄漏到任意页面）
        var __parentOrigin = resolveParentOrigin();
        window.parent.postMessage({type: 'iframeLoaded', source: 'chat'}, __parentOrigin);
    </script>
</body>
</html>`;
}

// 创建知识库界面的HTML内容
function createKnowHtmlContent() {
    return `<!doctype html>
<html>
<head>
    <meta charset="UTF-8">
    <title>Knowledge Base</title>
    <style>
        /* 基础样式 */
        html, body {
            background: #f8fafc;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            margin: 0;
            padding: 0;
            height: 100%;
            overflow: hidden;
        }

        #laneChatWidget {
            width: 100%;
            height: 100%;
            position: relative;
        }

        /* 知识库界面样式 */
        .know-container {
            height: 100%;
            width: 100%;
            position: relative;
            background: #fff;
            display: flex;
            flex-direction: column;
        }

        .loading {
            display: flex;
            justify-content: center;
            align-items: center;
            height: 100%;
            font-size: 14px;
            color: #666;
        }
    </style>
</head>
<body>
    <div id="laneChatWidget">
        <div class="know-container">
            <div class="loading">正在加载知识库...</div>
        </div>
    </div>

    <script>
        // 动态获取允许的origin - 支持开发和生产环境
        const allowedOrigins = [
            window.location.origin,
            "http://localhost:3000",
            "http://localhost:8787",
            "https://push.chatly.cc",
            "https://webmanager.lane.chat"
        ];

        // 从 document.referrer 推导宿主页 origin（iframe 内）
        function resolveParentOrigin() {
            try {
                if (document.referrer) {
                    var refOrigin = new URL(document.referrer).origin;
                    if (allowedOrigins.indexOf(refOrigin) !== -1) return refOrigin;
                    return refOrigin;
                }
            } catch (e) { /* ignore */ }
            // 最小安全默认值：当前窗口自身 origin
            return window.location.origin;
        }

        window.addEventListener("message", (event) => {
            if (allowedOrigins.indexOf(event.origin) === -1) return;
            if (event.data && event.data.type === 'scrollToBottom') {
                window.scrollTo(0, document.body.scrollHeight);
            } else if (event.data && event.data.type === 'updateContent') {
                // 处理内容更新
                if (event.data.content) {
                    document.getElementById('laneChatWidget').innerHTML = event.data.content;
                }
            }
        });

        // 通知父窗口iframe已加载（指定具体 target origin，避免泄漏到任意页面）
        var __parentOrigin = resolveParentOrigin();
        window.parent.postMessage({type: 'iframeLoaded', source: 'know'}, __parentOrigin);
    </script>
</body>
</html>`;
}

// 创建全局的公开 API 对象
//
// 两个全局名指向同一个对象引用（=== 相等）：
//   window.laneChat     —— 规范名（文档 / npm 包对齐此名）
//   window.laneChatSDK  —— 历史别名，保留以兼容存量集成，绝不移除
// 因为是同一 ref，任一句柄上调用的方法完全等价。
window.laneChatSDK = {
    _widgetInstance: null,
    _reactRoot: null,

    // 事件订阅表：{ event: [cb, ...] }。轻量 emitter，仅 'open'/'close' 两个事件。
    _listeners: {},

    /**
     * 订阅 widget 事件。目前支持：
     *   'open'  —— 聊天面板打开时（open()/openChat()/toggle() 打开）
     *   'close' —— 聊天面板关闭时（close()/toggle() 关闭）
     * 返回一个取消订阅函数。回调异常被吞掉，不影响 widget。
     *   const off = laneChat.on('open', () => {...}); off();
     */
    on: function(event, cb) {
        if (typeof cb !== 'function') return function() {};
        if (!this._listeners[event]) this._listeners[event] = [];
        this._listeners[event].push(cb);
        var self = this;
        return function() {
            var arr = self._listeners[event];
            if (!arr) return;
            var i = arr.indexOf(cb);
            if (i !== -1) arr.splice(i, 1);
        };
    },

    // 内部：派发事件给订阅者（fire-and-forget，回调异常隔离），并单向转发给 native bridge。
    // bridge 事件名与 JS 总线略有出入（open/close → opened/closed），未知事件不转发。
    _emit: function(event, payload) {
        var arr = this._listeners[event];
        if (arr) {
            arr.slice().forEach(function(cb) {
                try { cb(payload); } catch (e) {
                    console.warn('[Lane.Chat] event listener for "' + event + '" threw:', e && e.message);
                }
            });
        }
        var bridgeName = event === 'open' ? 'opened' : (event === 'close' ? 'closed' : event);
        var known = ['opened', 'closed', 'message_sent', 'message_received', 'unread_changed', 'session_loaded'];
        if (known.indexOf(bridgeName) !== -1) nativeBridge.event(bridgeName, payload || {});
    },

    // 初始化 Widget
    init: function() {
        try { window.__laneChatInitCallCount = (window.__laneChatInitCallCount || 0) + 1; } catch (_) {}
        return window.injectChat().then(function(inst) {
            // native bridge：探测到原生容器就握手；普通网页 init 返回 false 零影响。
            // 挂在 injectChat 的成功路径上，此时 _widgetInstance 已建立（React 18
            // createRoot 首次挂载是同步提交，componentDidMount 已经跑完）。
            try { nativeBridge.init(WIDGET_VERSION); } catch (e) { /* bridge 永不阻塞挂载 */ }
            return inst;
        });
    },

    // 销毁 Widget
    destroy: function() {
        return window.destroyChat();
    },

    // 打开聊天窗口
    // 'open'/'close' 事件不在这里 _emit —— widget.js componentDidUpdate 的
    // isChatOpen 跳变收敛点是唯一发送点（浮球点击/navigate-to-chat/inline 等所有
    // 打开路径都汇聚在那），这里再发一次会双发。这里只做状态跳转的功能调用。
    open: function() {
        if (this._widgetInstance) {
            this._widgetInstance.openChat();
        } else {
            console.warn('[Lane.Chat] Not initialized. Call init() first.');
        }
    },

    // 别名
    openChat: function() {
        this.open();
    },

    // 关闭聊天窗口
    close: function() {
        if (this._widgetInstance) {
            this._widgetInstance.closeChat();
        }
    },

    // 切换聊天窗口状态
    toggle: function() {
        if (this._widgetInstance) {
            this._widgetInstance.toggleChat();
        }
    },

    // 检查是否打开
    isOpen: function() {
        if (this._widgetInstance) {
            return this._widgetInstance.isChatOpen();
        }
        return false;
    },

    // 当前未读消息数（浮球徽标数字）。npm 包 laneChat.unreadCount() 依赖此方法。
    unreadCount: function() {
        return (this._widgetInstance && this._widgetInstance.getUnreadCount)
            ? this._widgetInstance.getUnreadCount() : 0;
    },

    // 隐藏浮球（不影响已打开的面板）。npm 包 laneChat.show()/hide() 依赖。
    show: function() {
        if (this._widgetInstance && this._widgetInstance.setLauncherHidden) this._widgetInstance.setLauncherHidden(false);
    },
    hide: function() {
        if (this._widgetInstance && this._widgetInstance.setLauncherHidden) this._widgetInstance.setLauncherHidden(true);
    },

    // 更新主题配置
    updateTheme: function(themeConfig) {
        if (this._widgetInstance) {
            this._widgetInstance.updateTheme(themeConfig);
        }
    },

    // 是否已挂载（幂等检查辅助方法，SPA 切换前可以用来判断是否需要 destroy）
    isMounted: function() {
        return window.__laneChatMounted === true && !!this._widgetInstance;
    },

    // 内部缓存：上一次 setFromData 的 payload，用于幂等比较
    // （暴露在 laneChatSDK 上方便 console 调试，但不算 public API）
    _lastFromData: null,

    /**
     * 设置/更新访客身份与自定义属性（规范方法名）。
     *   laneChat.identify({ email:'jane@acme.com', name:'Jane', plan:'pro' })
     * 标准字段（email / name / phone，键大小写不敏感，email 兼容 email_address、
     * phone 兼容 phone_number）会被服务端落到访客资料列（仅在为空时，冲突留痕）；
     * 其余键作为自定义属性存 from_data，客服面板可见。
     * 契约与 setFromData 完全一致（identify 就是 setFromData，语义更清晰的规范名）。
     */
    identify: function(traits) {
        return this.setFromData(traits);
    },

    // 推送/更新访客上下文（Crisp $crisp.push(["set", ...]) 风格）
    //   sdk.setFromData({uuid:'xxx', email:'...', plan_name:'pro'})
    // identify() 的历史别名，保留兼容存量集成，行为完全等价。
    // 契约：永远 in-place，永远 fire-and-forget，绝不重挂 widget。
    //   - 幂等：与 _lastFromData 深比较，相同则 noop
    //   - 未挂载：写 window.from_data，等 autoInit / 后续 init() 自己读
    //   - 已挂载但缺 chat_id/token：写 window.from_data + 更新 _lastFromData，等下次 chat 建立
    //   - 已挂载：POST /chat/context 就地更新；网络失败只 log，不重挂
    // 外部 integration 不应调 destroy()/init() —— setFromData/identify 已覆盖所有数据推送场景。
    setFromData: function(data) {
        var next = data || {};

        try {
            var prevJSON = this._lastFromData ? JSON.stringify(this._lastFromData) : null;
            var nextJSON = JSON.stringify(next);
            if (prevJSON !== null && prevJSON === nextJSON) {
                return;
            }
            this._lastFromData = next;
        } catch (_) { /* JSON.stringify 失败 → 跳过幂等 */ }

        try { window.from_data = next; } catch (_) {}

        if (!this.isMounted()) {
            // 未挂载：window.from_data 已写，autoInit / 外部 init() 起来后会读到
            return;
        }

        var self = this;
        var inst = self._widgetInstance;

        var chatId = null;
        try {
            var chatInst = inst && inst.chatFrameRef
                && inst.chatFrameRef.current
                && typeof inst.chatFrameRef.current.getChatInstance === 'function'
                ? inst.chatFrameRef.current.getChatInstance()
                : null;
            if (chatInst) {
                chatId = chatInst.chat_id || (chatInst.state && chatInst.state.chat_id) || null;
            }
        } catch (_) { /* ignore */ }

        var token = null;
        try {
            token = (ApiService && typeof ApiService.getToken === 'function')
                ? ApiService.getToken()
                : (ApiService ? ApiService._token : null);
        } catch (_) { /* ignore */ }

        // chat 未建立 / token 未就绪：window.from_data 已写，等下一次 chat 建立时自动带上
        if (!chatId || !token) return;

        var baseURL = (ApiService && ApiService.baseURL) ? ApiService.baseURL : '';
        if (!baseURL) return;

        try {
            fetch(baseURL + '/chat/context', {
                method: 'POST',
                cache: 'no-store',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + token
                },
                body: JSON.stringify({ chat_id: chatId, from_data: next })
            }).then(function(r) {
                if (!r.ok) {
                    return Promise.reject(new Error('HTTP ' + r.status));
                }
                return r.json().catch(function() { return null; });
            }).catch(function(e) {
                console.warn('[Lane.Chat] setFromData in-place update failed (no remount):', e && e.message);
            });
        } catch (e) {
            console.warn('[Lane.Chat] setFromData threw (no remount):', e && e.message);
        }
    },

    /**
     * 登出：清掉身份、会话数据和本机缓存的访客状态，然后以全新匿名访客重新挂载。
     * 没挂载过就只清状态，不会替宿主挂载。
     * 保留合法的非访客缓存：lane_widget_config_、主题、声音偏好。
     * 返回 Promise，重新挂载完成后 resolve。
     */
    reset: function() {
        var self = this;
        var wasMounted = window.__laneChatMounted === true || !!_inFlightInject;

        self._pendingSessionData = null;
        self._sentSessionData = null;
        self._sentSessionChatId = null;
        if (self._sessionDataTimer) {
            clearInterval(self._sessionDataTimer);
            self._sessionDataTimer = null;
        }
        self._lastFromData = null;
        try { window.from_data = {}; } catch (_) {}
        try { delete window.laneChatExternalUserId; } catch (_) { window.laneChatExternalUserId = undefined; }

        var settled = _inFlightInject ? _inFlightInject.catch(function() {}) : Promise.resolve();
        return settled.then(function() {
            window.__laneChatPendingDestroy = false;
            if (window.__laneChatPendingDestroyTimer) {
                clearTimeout(window.__laneChatPendingDestroyTimer);
                window.__laneChatPendingDestroyTimer = null;
            }
            _performDestroy();
            _clearStoredVisitor(window.laneChatAppId);
            try { ApiService.setToken(null); } catch (_) {}
            self._emit('unread_changed', { count: 0 });
            return wasMounted ? self.init() : undefined;
        });
    },

    // 内部：当前会话 id（未建立返回 null）
    _currentChatId: function() {
        try {
            var inst = this._widgetInstance;
            var chatInst = inst && inst.chatFrameRef
                && inst.chatFrameRef.current
                && typeof inst.chatFrameRef.current.getChatInstance === 'function'
                ? inst.chatFrameRef.current.getChatInstance()
                : null;
            if (!chatInst) return null;
            return chatInst.chat_id || (chatInst.state && chatInst.state.chat_id) || null;
        } catch (_) { return null; }
    },

    // 内部：访客 JWT（未就绪返回 null）
    _visitorToken: function() {
        try {
            return (ApiService && typeof ApiService.getToken === 'function')
                ? ApiService.getToken()
                : (ApiService ? ApiService._token : null);
        } catch (_) { return null; }
    },

    // 内部：尚未送达的会话数据（chat/token 未就绪时先攒着）
    _pendingSessionData: null,
    _sessionDataTimer: null,
    // 内部：已经发出去的键值快照，用来跳过重复调用。只对 _sentSessionChatId
    // 这一条会话有效 —— 访客开了新会话，同样的值要重新写进去。
    _sentSessionData: null,
    _sentSessionChatId: null,

    /**
     * 设置/更新**本次会话**的自定义数据（chats.from_data，按键合并）。
     *   laneChat.setSessionData({ cart_items: 3, cart_total: '99.00' })
     *
     * 与 identify()/setFromData() 的分工：
     *   - identify()       → 跟**人**，跨会话保留（clients.from_data）
     *   - setSessionData() → 只跟**本次会话**，会话结束即定格（chats.from_data）
     *
     * 契约：合并（只动传入的键，值传 null 删键）、in-place、fire-and-forget，
     * 绝不重挂 widget。会话/token 未就绪时先攒在本地，建立后自动补发。
     */
    setSessionData: function(data) {
        if (!data || typeof data !== 'object' || Array.isArray(data)) {
            console.warn('[Lane.Chat] setSessionData expects a plain object');
            return;
        }

        // 先滤掉没变化的键。宿主常把这个挂在 useEffect / watch 上，每次渲染都调一遍；
        // 不滤就是每次渲染一个请求。服务端是合并语义，少发这些键没有任何差别。
        var sent = (this._sentSessionData && this._sentSessionChatId === this._currentChatId())
            ? this._sentSessionData
            : {};
        var changed = null;
        for (var k in data) {
            if (!Object.prototype.hasOwnProperty.call(data, k)) continue;
            var isSame = Object.prototype.hasOwnProperty.call(sent, k) && sent[k] === data[k];
            if (isSame) continue;
            (changed = changed || {})[k] = data[k];
        }
        if (!changed) return;

        // 攒起来：会话未建立时多次调用在客户端先合并，建立后一次发出去。
        var pending = this._pendingSessionData || {};
        for (var ck in changed) {
            if (Object.prototype.hasOwnProperty.call(changed, ck)) pending[ck] = changed[ck];
        }
        this._pendingSessionData = pending;

        this._flushSessionData();
    },

    _flushSessionData: function() {
        var self = this;
        var pending = self._pendingSessionData;
        if (!pending) return;

        var chatId = self._currentChatId();
        var token = self._visitorToken();
        var baseURL = (ApiService && ApiService.baseURL) ? ApiService.baseURL : '';

        if (!chatId || !token || !baseURL) {
            // 会话还没建立（访客还没开面板/还没说话）。集成方常在页面加载就调用，
            // 这时干等是对的 —— 但要主动回来看，否则这批数据永远躺在内存里。
            if (!self._sessionDataTimer) {
                // 只读内存、不发请求，所以盯多久都没有网络成本。访客可能加载几分钟后才开
                // 面板，不能设上限 —— 文档承诺的是"会话开始后送达"。
                self._sessionDataTimer = setInterval(function() {
                    if (!self._pendingSessionData) {
                        clearInterval(self._sessionDataTimer);
                        self._sessionDataTimer = null;
                        return;
                    }
                    self._flushSessionData();
                }, 1500);
            }
            return;
        }

        if (self._sessionDataTimer) {
            clearInterval(self._sessionDataTimer);
            self._sessionDataTimer = null;
        }

        // 先摘走再发：发送期间新来的调用会重开一批，不会被这次的成功回调清掉。
        self._pendingSessionData = null;

        try {
            fetch(baseURL + '/chat/session-data', {
                method: 'POST',
                cache: 'no-store',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + token
                },
                body: JSON.stringify({ chat_id: chatId, data: pending })
            }).then(function(r) {
                if (!r.ok) return Promise.reject(new Error('HTTP ' + r.status));
                return r.json();
            }).then(function(body) {
                // 业务错误走 HTTP 200 + 非零 code，只看 r.ok 会把 422 当成功。
                if (!body || body.code !== 0) {
                    return Promise.reject(new Error((body && body.msg) || 'rejected'));
                }
                // 只有服务端收下了才记进"已发"快照 —— 失败还记的话，宿主重试同样的值
                // 会被去重挡掉，数据就永远上不去了。
                var snapshot = (self._sentSessionData && self._sentSessionChatId === chatId)
                    ? self._sentSessionData
                    : {};
                for (var sk in pending) {
                    if (Object.prototype.hasOwnProperty.call(pending, sk)) snapshot[sk] = pending[sk];
                }
                self._sentSessionData = snapshot;
                self._sentSessionChatId = chatId;
            }).catch(function(e) {
                console.warn('[Lane.Chat] setSessionData update failed:', e && e.message);
            });
        } catch (e) {
            console.warn('[Lane.Chat] setSessionData threw:', e && e.message);
        }
    }
};

// 规范全局名：window.laneChat 与 window.laneChatSDK 指向同一对象引用（=== 相等）。
// 文档 / npm 包用 laneChat；laneChatSDK 作为历史别名永久保留。
window.laneChat = window.laneChatSDK;

// native → widget `navigate` 事件（SDK Bridge Protocol v1，docs/SDK_BRIDGE_PROTOCOL.md）：
// 通知栏点开 / App 内跳转时 WebView 已经开着的热切换，复用与 widget.html
// ?view=chat&chat_id= 完全相同的内部导航方法（Widget.switchToChat/navigateHome），
// 不发明第二条路径。home 分支特意走 navigateHome 而不是 switchToHome——原生容器
// 发来的 view:'home' 不能越过租户在 dash 关掉 Home 首页这条门（fix round 1）。
// 模块级只注册一次——bridge 单例常驻，_widgetInstance 在 componentDidMount 里随
// 每次挂载更新，这里按需读取，不必在每次 init() 时重新订阅/去重（destroy()+init()
// 合并场景见上方注释）。
function applyNativeNavigate(target, sourceEvent) {
    if (!target) {
        debugLog('[Lane.Chat] ' + sourceEvent + ': unknown view ignored');
        return;
    }
    var inst = window.laneChatSDK && window.laneChatSDK._widgetInstance;
    if (!inst) return; // 尚未挂载：native 理论上不会在 helloAck 之前发业务事件
    if (target.view === 'chat') {
        if (target.chatId) inst.switchToChat({ chatId: target.chatId });
        else inst.switchToChat();
    } else if (target.view === 'home' && typeof inst.navigateHome === 'function') {
        // navigateHome (not switchToHome directly) — gates on tenant's
        // homePageEnabled same as handleListButtonClick; a native host can't
        // force-render a Home view the tenant disabled in dash.
        inst.navigateHome();
    }
}

nativeBridge.onNativeEvent('navigate', function (payload) {
    applyNativeNavigate(resolveNavigateTarget(payload), 'navigate');
});

// `deeplink` 是三端 native **实际在发**的那个事件（ChatWebViewController.kt /
// LaneChatWebView.swift / macOS 同名方法），载荷是 `{url}`；上面的 `navigate`
// 收的是 `{view, chatId}`，而 navigate **三端都不发**。两半各自写完、各自有
// 测试，中间那一跳一直没人接：WebView 已经开着时的热切换（通知栏点开、App 内
// 跳转）在三端全部静默失效，不报错、不打日志。
// 订阅两个名字而不是改三端：native 是已发布的固定版本，改 widget 一处即可覆盖
// 所有已在野的 SDK；两条路汇进同一个 applyNativeNavigate，不会长出第二套语义。
nativeBridge.onNativeEvent('deeplink', function (payload) {
    applyNativeNavigate(navigateTargetFromDeepLink(payload), 'deeplink');
});

// 自动初始化逻辑 - 支持手动初始化模式
//
// 关键：widget.js 通常用 <script async> 加载。在 SPA 宿主里（比如 Angela，路由 mount
// 后才注入 extension.html），window 'load' 事件可能早就 fired，addEventListener 永远
// 不会触发 → autoInit 不跑 → 客服按钮永远不出现。
// 所以同时支持两条路径：
//   1. document.readyState 已是 'complete' / 'interactive' → 立即调（SPA 场景）
//   2. 否则注册 load 事件等待（首次 cold-load 场景）
if (typeof document !== 'undefined' &&
    (document.readyState === 'complete' || document.readyState === 'interactive')) {
    // 微任务里跑，避免阻塞当前调用栈
    setTimeout(autoInit, 0);
} else if (window.attachEvent) {
    window.attachEvent('onload', autoInit);
} else {
    window.addEventListener('load', autoInit, false);
}

// 自动初始化函数
//
// 默认行为：window load 后自动 injectChat（99% 集成方只需 <script> 标签 + app-id 即可）。
//
// 两个关闭开关（集成方需完全接管生命周期时用）：
//   - window.laneChatNoAutoInit = true  → autoInit 不跑
//   - window.laneChatManualInit = true  → 同上（语义更清楚，兼容 Angela 等已使用此名的集成）
//
// 为什么需要 manual init 逃生口：像 Angela 这种宿主会在自己的 access-point 回调里
// 同步调 sdk.destroy() / sdk.init() 来响应登录状态变化。如果 autoInit 同时跑，会
// 和宿主的 destroy+init 撞车 → "按钮闪一下消失"。宿主显式关掉 autoInit 即可避免。
function autoInit() {
    try { window.__laneChatAutoInitFired = (window.__laneChatAutoInitFired || 0) + 1; } catch (_) {}
    if (window.laneChatNoAutoInit || window.laneChatManualInit) {
        try { window.__laneChatAutoInitMode = 'disabled'; } catch (_) {}
        return;
    }
    try { window.__laneChatAutoInitMode = 'auto'; } catch (_) {}
    try { injectChat(); } catch (e) {
        try { window.__laneChatAutoInitError = String(e && e.message || e); } catch (_) {}
    }
}

// 合并 destroy()+init() 连续调用。典型场景：Angela 等宿主在 access-point 回调里写
//   apply(sdk) { sdk.destroy(); sdk.init(); }
// 每次 user / plan / authToken emit 都重跑一遍。不合并的话 widget 会反复"闪"。
//
// 两级合并：
//   (a) inject 还在跑时 destroy() → 只标记 pending，init() 会撤销
//   (b) mount 完成后 destroy() → 延迟 150ms 再真卸载；窗口内 init() 会撤销并把
//       window.from_data 通过 setFromData 就地更新（等价于 Crisp "push data"）
//
// 并发 init() 去重：多个 Observable 同时 emit 会并发调 init()，同一时间只允许一个
// injectChat 真跑，其它复用 in-flight promise。
let _inFlightInject = null;

async function injectChat() {
    // (a) 撤销 inject-in-progress 期间的 pending destroy
    if (window.__laneChatPendingDestroy === true) {
        window.__laneChatPendingDestroy = false;
        if (window.laneChatSDK && window.laneChatSDK._widgetInstance) {
            return window.laneChatSDK._widgetInstance;
        }
    }

    // (b) 撤销已排队的延迟 destroy，顺带就地更新 from_data
    if (window.__laneChatPendingDestroyTimer) {
        clearTimeout(window.__laneChatPendingDestroyTimer);
        window.__laneChatPendingDestroyTimer = null;
        try { window.__laneChatCoalesceHitCount = (window.__laneChatCoalesceHitCount || 0) + 1; } catch (_) {}
        try {
            if (window.from_data && window.laneChatSDK && typeof window.laneChatSDK.setFromData === 'function') {
                window.laneChatSDK.setFromData(window.from_data);
            }
        } catch (_) {}
        if (window.laneChatSDK && window.laneChatSDK._widgetInstance) {
            return window.laneChatSDK._widgetInstance;
        }
    }

    // 并发 init 去重
    if (_inFlightInject) return _inFlightInject;

    window.__laneChatInjectInProgress = true;
    _inFlightInject = (async () => {
        try {
            return await _injectChatImpl();
        } finally {
            window.__laneChatInjectInProgress = false;
            _inFlightInject = null;
            if (window.__laneChatPendingDestroy === true) {
                window.__laneChatPendingDestroy = false;
                destroyChat();
            }
        }
    })();
    return _inFlightInject;
}

async function _injectChatImpl() {
    // —— 幂等性检查（多实例防重复挂载）——
    // 第一道防线：全局挂载标志
    if (window.__laneChatMounted === true) {
        // 如果 DOM 节点还在，直接复用；否则清掉标志重来
        const existingRoot = document.getElementById('laneChatRoot');
        if (existingRoot && window.laneChatSDK && window.laneChatSDK._widgetInstance) {
            console.warn('[Lane.Chat] Already mounted (reusing existing instance). Call destroy() first to remount fresh.');
            return window.laneChatSDK._widgetInstance;
        }
        // DOM 丢失但标志还在 —— 说明被外部清理过，重置标志继续挂载
        console.warn('[Lane.Chat] Stale mount flag detected, resetting.');
        window.__laneChatMounted = false;
    }

    // 第二道防线：DOM 节点检查
    const existingNode = document.getElementById('laneChatRoot');
    if (existingNode) {
        // 如果 React root 还在就直接返回，不重复 createElement
        if (window.laneChatSDK && window.laneChatSDK._reactRoot) {
            console.warn('[Lane.Chat] #laneChatRoot already exists (reusing).');
            window.__laneChatMounted = true;
            return window.laneChatSDK._widgetInstance;
        }
        // DOM 残留但 root 丢失 —— 清掉节点重建
        console.warn('[Lane.Chat] Orphan #laneChatRoot found, removing and recreating.');
        if (existingNode.parentNode) existingNode.parentNode.removeChild(existingNode);
    }

    // 防止重复初始化（兼容旧检查）
    if (window.laneChatSDK && window.laneChatSDK._widgetInstance) {
        console.warn('[Lane.Chat] Already initialized. Call destroy() first to reinitialize.');
        return window.laneChatSDK._widgetInstance;
    }

    debugLog('[Lane.Chat] Initializing...');

    // 原生容器系统主题提示透传：widget.html 的 boot script 把 ?sys_theme=
    // 白名单解析进 window.laneChatSysTheme，这里喂给 theme-color.js 的模块级
    // 提示位——widget.js 里 resolveEffectiveTheme 的 auto 档零改动即优先吃它
    // （WebView 内 prefers-color-scheme 不可靠，见 theme-color.js 注释）。
    // 浏览器/JS-SDK 场景没有这个 window 变量 → 提示位清空 → 行为不变。
    setSysThemeHint(window.laneChatSysTheme);

    const [{createRoot}] = await Promise.all([
        import('react-dom/client')
    ]);

    // 支持 data-app-key 属性（从 <script> 标签读取）。
    // 注意：这里在 await 之后运行，document.currentScript 已是 null 或指向别的脚本，
    // 不能依赖它。直接按属性选择 loader 脚本 —— data-app-key 全页唯一。
    if (!window.laneChatAppId) {
        const keyScript = document.querySelector('script[data-app-key]');
        if (keyScript && keyScript.getAttribute('data-app-key')) {
            window.laneChatAppId = keyScript.getAttribute('data-app-key');
        }
    }

    // data-mode / data-container 从 script 标签读取（inline embed 场景）。
    // 同样不能依赖 document.currentScript（await 后已失效），按属性直接选。
    if (!window.laneChatMode) {
        const modeScript = document.querySelector('script[data-mode]');
        if (modeScript && modeScript.getAttribute('data-mode')) {
            window.laneChatMode = modeScript.getAttribute('data-mode');
        }
    }
    if (!window.laneChatContainer) {
        const containerScript = document.querySelector('script[data-container]');
        if (containerScript && containerScript.getAttribute('data-container')) {
            window.laneChatContainer = containerScript.getAttribute('data-container');
        }
    }

    // 解析 inline 模式宿主容器（可接受 selector 字符串 / DOM 节点）
    let __inlineHost = null;
    if (window.laneChatMode === 'inline') {
        const c = window.laneChatContainer;
        try {
            __inlineHost = (typeof c === 'string') ? document.querySelector(c)
                         : (c && c.nodeType === 1) ? c
                         : null;
        } catch (e) { __inlineHost = null; }
        if (!__inlineHost) {
            console.error('[Lane.Chat] Inline mode requires window.laneChatContainer to resolve to a DOM element. Falling back to floating mode.');
            window.laneChatMode = 'floating';
        }
    }

    if (!window.laneChatAppId) {
        console.error('[Lane.Chat] Please set window.laneChatAppId or use data-app-key attribute');
    } else {
        // 使用配置中的服务器地址
        const server = window.laneChatServerUrl || window.location.origin;

        // 如果需要，加载widget导航处理脚本（本地版本）
        // 默认禁用，因为大多数情况下不需要（Service Worker 导航专用）
        if (window.laneChatLoadNavScript === true) {
            const navScript = document.createElement('script');
            navScript.src = `${server}/widget-navigation-handler.js`;
            navScript.async = true;
            navScript.onerror = () => {
                console.warn('[Lane.Chat] Failed to load navigation handler script');
            };
            document.head.appendChild(navScript);
        }

        const domNode = document.createElement('div');
        domNode.id = 'laneChatRoot';

        // Inline 模式：挂到宿主 div 里，继承其宽高；同时让宿主成为定位上下文（position:relative）。
        // 浮动模式：仍然挂到 document.body（原有行为）。
        if (window.laneChatMode === 'inline' && __inlineHost) {
            // 尊重宿主已有的 position；仅在 static 时升级为 relative（成为 absolute/emoji-portal 的祖先）
            try {
                const cs = window.getComputedStyle(__inlineHost);
                if (cs && (!cs.position || cs.position === 'static')) {
                    __inlineHost.style.position = 'relative';
                }
            } catch (_) { /* ignore */ }
            // Host 宽高由用户控制；widget 自己 width/height 100% 铺满
            domNode.style.width = '100%';
            domNode.style.height = '100%';
            __inlineHost.appendChild(domNode);
        } else {
            document.getElementsByTagName('body')[0].appendChild(domNode);
        }

        // 检查Shadow DOM支持
        const supportsShadowDOM = 'attachShadow' in Element.prototype && 'ShadowRoot' in window;

        // 结构层 token 也要落到宿主文档 :root 上：非 Shadow DOM 降级路径靠它，
        // 且 widget.html 宿主页的容器层（骨架屏交接的那一层）同样读这批变量。
        // 幂等，重复 init 不堆叠。
        ensureStructureTokenStyle(document);

        let renderContainer;
        let shadowRoot; // 提升到外部作用域

        if (supportsShadowDOM) {
            // 使用Shadow DOM
            shadowRoot = domNode.attachShadow({ mode: 'closed' });

            // 在Shadow DOM中添加样式
            const shadowStyles = document.createElement('style');
            shadowStyles.textContent = `
                /* Shadow DOM内的完全隔离样式 */
                :host {
                    /* 不设置 position/size，让它由内容决定 */
                    /* Widget 组件内部会自己定位 */
                    display: block;
                }

                /* ---- 结构层设计 token（圆角/间距/阴影/动效，spec 2026-08-08）----
                   静态注入、**不等任何配置**（与颜色 token 不同，见
                   theme-color.js 的分层说明）：首帧就可用，shadow 内所有
                   radius, space, shadow and duration tokens
                   都从这里继承。暗色阴影由 widget.js _syncThemeCssVars 在主题
                   解析后用内联样式覆写（几何不变，只换色）。 */
                ${structureTokensCssText(':host')}

                /* 基础重置 */
                * {
                    box-sizing: border-box;
                    margin: 0;
                    padding: 0;
                    /* 兜底：iOS/Android 点击灰色蒙层。closeButtonStyle/fabCloseButtonStyle
                       等确实可点的元素本来就各自内联设了这行（style.js），这里在 shadow
                       root 级别再兜一层——防止以后新增的可点元素又忘记单独设置（bug 2：
                       header 挂了 tab 点击委托的 onClick，却唯独漏了这行，点击时露出
                       原生 tap-highlight 深色蒙层）。 */
                    -webkit-tap-highlight-color: transparent;
                }

                /* 字体和文本 */
                /* 暗色主题说明：--lc-* 变量只在暗色生效时由 widget.js
                   _syncThemeCssVars 写到 shadow host 上；亮色下所有 var() 走
                   fallback，计算值与历史逐字节一致（亮色像素回归的硬保障）。 */
                html, body, div {
                    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
                    font-size: 14px;
                    line-height: 1.5;
                    color: var(--lc-text, #212529);
                }

                /* Widget容器 - 不占据空间，让内部组件自己定位 */
                .widget-root {
                    /* 不设置任何尺寸和定位，完全由内部组件控制 */
                }

                /* 表单元素重置 */
                button {
                    font-family: inherit;
                    font-size: inherit;
                    line-height: inherit;
                    margin: 0;
                    padding: 0;
                    border: none;
                    background: none;
                    color: inherit;
                    cursor: pointer;
                    outline: none;
                    -webkit-appearance: none;
                    -moz-appearance: none;
                    appearance: none;
                }

                input, textarea {
                    font-family: inherit;
                    font-size: inherit;
                    line-height: inherit;
                    margin: 0;
                    padding: 0;
                    border: none;
                    background: none;
                    color: inherit;
                    outline: none;
                    -webkit-appearance: none;
                    -moz-appearance: none;
                    appearance: none;
                }

                /* 动画定义 */
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }

                /* 完整的聊天样式 */
                .chat {
                    list-style: none;
                    background: var(--lc-bg, #f8fafc);
                    padding: var(--lc-space-4);
                    margin: 0;
                    transition: padding-bottom var(--lc-dur-slow) var(--lc-ease-out);
                }

                .chat-container{
                    /* Flex child of the panel's flex-column: fill space ABOVE the
                       bottom bar and scroll internally. (Floating widget only —
                       the inline iframe uses chat-frame.js's absolute layout.)
                       Fixes messages/emoji-picker hiding behind the toolbar. */
                    flex: 1 1 auto;
                    min-height: 0;
                    position: relative;
                    width: 100%;
                    overflow: auto;
                    scroll-behavior: smooth;
                    -webkit-overflow-scrolling: touch;
                }

                /* 移动端聊天容器调整 */
                @media only screen and (max-width: 500px) {
                    .chat-container {
                        /*height: calc(100% - 70px) !important;  移动端输入区域稍高 */
                    }
                }

                /* 必须是直接子代。用后代选择器 .chat li 会连 markdown 渲染出来的
                   列表项一起匹配 —— AI 回答最常见的格式就是「- **标签** — 说明」，
                   display:flex 把 <strong> 和后面的文字拆成两个 flex item 并排，
                   标签被挤成一条窄列、说明吊在右边，整段没法读。
                   消息行是 <ol class="chat"> 的直接子元素，> 是安全的。 */
                .chat > li {
                    padding: var(--lc-space-1) 0;
                    font-size: 14px;
                    overflow: hidden;
                    display: flex;
                    cursor: default;
                    user-select: text;
                }

                /* markdown 正文里的列表 —— 显式声明成正常列表项，
                   免得将来又有哪条 .chat 下的规则漏进来。 */
                .lc-markdown ul,
                .lc-markdown ol {
                    display: block;
                }
                .lc-markdown li {
                    display: list-item;
                    padding: 0;
                    margin: 0.15em 0;
                    overflow: visible;
                    font-size: inherit;
                    cursor: auto;
                }

                /* 消息分组节奏：组内 4px、组间（发送者切换）12px。admin 与 agent 同侧同规则 */
                .chat > li + li.visitor,
                .chat > li.visitor + li.visitor {
                    padding-top: var(--lc-space-1);
                }

                .chat > li + li.agent,
                .chat > li.agent + li.agent,
                .chat > li + li.admin,
                .chat > li.admin + li.admin {
                    padding-top: var(--lc-space-1);
                }

                .chat > li.visitor + li.agent,
                .chat > li.agent + li.visitor,
                .chat > li.visitor + li.admin,
                .chat > li.admin + li.visitor {
                    padding-top: var(--lc-space-3);
                }

                .visitor {
                    justify-content: flex-end;
                    align-items: flex-end;
                }

                .visitor .msg {
                    order: 1;
                    background: var(--lc-bubble-user-bg, var(--widget-main-color, #2563eb));
                    color: var(--lc-bubble-user-text, #fff);
                    border-radius: var(--lc-radius-bubble-out);
                    border: none;
                    box-shadow: var(--lc-shadow-1);
                }

                .admin .msg, .agent .msg {
                    order: 1;
                    background: var(--lc-bubble-agent-bg, #f1f5f9);
                    color: var(--lc-bubble-agent-text, #1e293b);
                    border-radius: var(--lc-radius-bubble-in);
                    border: none;
                    box-shadow: var(--lc-shadow-1);
                }

                .agent {
                    justify-content: flex-start;
                    align-items: flex-start;
                }

                .msg-avatar {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                }

                .msg-wrapper {
                    display: flex;
                    flex-direction: column;
                    align-items: flex-start;
                    width: fit-content;
                    max-width: 80%;
                }

                .visitor .msg-wrapper {
                    align-items: flex-end;
                }

                .agent-name {
                    font-weight: 500;
                    line-height: 1.4;
                    padding-left: var(--lc-space-1);
                }

                .msg {
                    word-wrap: break-word;
                    background: var(--lc-surface, white);
                    min-width: 50px;
                    max-width: 100%;
                    /* 横向 space-3(12)：14 不在 4/8/12/16/24/32 的间距标尺上，是历史
                       字面量，Android/iOS 一直是 12（裁决表 R2）。纵向 10 保持不动 ——
                       动它会改变气泡高度，牵动列表高度缓存与滚动锚点。 */
                    padding: 10px var(--lc-space-3);
                    border-radius: var(--lc-radius-lg);
                    box-shadow: var(--lc-shadow-1);
                    line-height: 1.5;
                    transition: box-shadow var(--lc-dur-base) var(--lc-ease-out);
                }

                .msg:hover {
                    box-shadow: var(--lc-shadow-2);
                }

                .msg p {
                    margin: 0 0 3.2px 0;
                    margin: 0 0 0.2rem 0;
                }

                .msg .time {
                    font-size: 11px;
                    color: var(--lc-text-3, #94a3b8);
                    margin-top: 3px;
                    float: right;
                    cursor: default;
                    user-select: none;
                }

                /* .textarea 样式已统一到 chat-frame.js 的 .chat-frame-container .msg-input textarea，
                   此处删除以消除两套 !important 冲突（半径/最大高度/overflow 曾互相打架）。 */

                /* 输入区域容器 */
                .msg-input {
                    box-sizing: border-box !important;
                    padding: 10px 12px !important;
                    /* iPhone 手势条机型：inset:0 的面板贴底，没有这个会盖住/贴住输入框。
                       env() 在不支持的设备上回退到 0，向后兼容。 */
                    padding-bottom: calc(10px + env(safe-area-inset-bottom, 0px)) !important;
                    position: absolute !important;
                    left: 0 !important;
                    right: 0 !important;
                    bottom: 0 !important;
                    z-index: 1000 !important;
                    background: var(--lc-surface, #ffffff) !important;
                    pointer-events: auto;
                    display: flex !important;
                    align-items: center !important;
                    gap: var(--lc-space-2) !important;
                    border-top: var(--lc-hairline);
                    box-shadow: var(--lc-shadow-1);
                }

                /* 工具栏样式 - PC端在输入框内部 */
                .toolbar{
                    height: auto !important;
                    padding: 0 !important;
                    display: flex !important;
                    flex-direction: row !important;
                    flex-wrap: nowrap !important;
                    align-items: center !important;
                    position: relative !important;
                    background: transparent !important;
                    gap: var(--lc-space-1) !important;
                    border: none !important;
                    box-shadow: none !important;
                    flex-shrink: 0 !important;
                }

                /* 滚动容器 */
                .toolbar-scroll-container {
                    flex: 1 !important;
                    display: flex !important;
                    flex-direction: row !important;
                    flex-wrap: nowrap !important;
                    align-items: center !important;
                    gap: var(--lc-space-1) !important;
                    overflow-x: auto !important;
                    overflow-y: hidden !important;
                    scroll-behavior: smooth !important;
                    -webkit-overflow-scrolling: touch !important;
                    scrollbar-width: none !important;
                    -ms-overflow-style: none !important;
                }

                .toolbar-scroll-container::-webkit-scrollbar {
                    display: none !important;
                }

                /* 滚动按钮 - 默认隐藏，移动端显示 */
                .toolbar-scroll-btn {
                    display: none !important;
                    flex-shrink: 0 !important;
                    width: 32px !important;
                    height: 32px !important;
                    border-radius: var(--lc-radius-lg) !important;
                    border: none !important;
                    background-color: rgba(0, 0, 0, 0.05) !important;
                    color: var(--lc-text-2, #666) !important;
                    cursor: pointer !important;
                    align-items: center !important;
                    justify-content: center !important;
                    transition: background-color var(--lc-dur-base) var(--lc-ease-out) !important;
                    padding: 0 !important;
                }

                .toolbar-scroll-btn:hover {
                    background-color: rgba(0, 0, 0, 0.1) !important;
                }

                .toolbar-scroll-btn:active {
                    background-color: rgba(0, 0, 0, 0.15) !important;
                    transform: scale(0.95) !important;
                }

                .chat-header{
                    display: flex;
                    margin: 0 auto;
                }

                .chat-header-sub{
                    display: flex;
                    margin: 0 auto;
                    z-index: 1;
                }

                /* Chat/Help/Home 三个 tab 的基类。
                   margin / padding / border-radius 三条**去掉了 !important**:
                   三个 tab 变体各自在内联样式里设这三个值(home 是 32px 圆形、
                   pill 是胶囊、underline 是等宽档),而内联样式盖不过 !important
                   —— 于是 Home 按钮写的是 32 圆形,实渲染却是 72×32 的圆角矩形,
                   pill 的胶囊也是钝的。每个开了 Home 的商户、每个访客、每次打开
                   面板的第一屏都是这个样子。
                   同时删掉了后面重复的 border-radius:sm / padding:5px 两条:
                   它们此前被 !important 完全遮蔽(死声明),去掉 !important 后按
                   层叠规则反而会由它们胜出,基线就变了 —— 一起删掉,基线保持原样。
                   background:#0057D7 是写死的蓝,三个变体都在内联里覆盖掉了,
                   属于同一块遗留物,但改它会动到覆盖链,留待单独处理。 */
                .chat-small {
                    line-height: 30px !important;
                    pointer-events: auto !important;
                    margin: 0 1px;
                    padding: 0 20px;
                    display: flex;
                    align-items: center !important;
                    border-radius: var(--lc-radius-md);
                    font-size: 16px;
                    background: #0057D7;
                }

                .send-btn{
                    background: var(--widget-main-color, #2563eb) !important;
                    text-align: center !important;
                    height: 36px !important;
                    width: 36px !important;
                    margin-inline-end: -1px !important;
                    margin-top: -3px !important;
                    display: block !important;
                    border-radius: var(--lc-radius-full) !important;
                    transition: transform var(--lc-dur-base) var(--lc-ease-out), background var(--lc-dur-base) var(--lc-ease-out) !important;
                }

                .send-btn:hover {
                    transform: scale(1.05) !important;
                    filter: brightness(0.9) !important;
                }

                .send-img{
                    background-color: #FFFFFF !important;
                    --mask-image: url(data:image/svg+xml;base64,PHN2ZyBoZWlnaHQ9IjE2IiB3aWR0aD0iMTYiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNsaXBQYXRoIGlkPSJhIj48cGF0aCBkPSJNMTQuODQuMDU0YS44NjQuODY0IDAgMDExLjEwNyAxLjEwN2wtNS4xODkgMTQuMjdhLjg2NC44NjQgMCAwMS0xLjQyMy4zMTZMNi4xNSAxMi41NmEuODY0Ljg2NCAwIDAxLS4wNTYtMS4xNmw3LjAzLTguNTIyTDQuNiA5LjkwOGEuODY0Ljg2NCAwIDAxLTEuMTYtLjA1NkwuMjUyIDYuNjY2QS44NjQuODY0IDAgMDEuNTcgNS4yNDN6Ii8+PC9jbGlwUGF0aD48ZyBjbGlwLXBhdGg9InVybCgjYSkiIHRyYW5zZm9ybT0icm90YXRlKDQ1IDYuNTE2IDQuMzQxKSI+PHBhdGggZD0iTTAgMGgxNnYxNkgweiIvPjwvZz48L3N2Zz4=) !important;
                    -webkit-mask-image: var(--mask-image) !important;
                    mask-image: var(--mask-image) !important;
                    -webkit-mask-size: contain !important;
                    mask-size: contain !important;
                    -webkit-mask-repeat: no-repeat !important;
                    mask-repeat: no-repeat !important;
                    -webkit-mask-position: center !important;
                    mask-position: center !important;
                    display: inline-block !important;
                    width: 15px !important;
                    height: 15px !important;
                    margin-top: 9px !important;
                    margin-inline-start: 1px !important;
                }

                /* 移动端样式优化 */
                @media only screen and (max-width: 500px) {
                    .msg-input {
                        padding: 8px 10px !important;
                        padding-bottom: calc(8px + env(safe-area-inset-bottom, 0px)) !important;
                        flex-wrap: wrap !important;
                    }

                    .toolbar {
                        gap: var(--lc-space-2) !important;
                    }

                    .toolbar a {
                        width: 38px !important;
                        height: 38px !important;
                        border-radius: var(--lc-radius-sm) !important;
                    }

                    .uploadimg {
                        width: 40px !important;
                        height: 40px !important;
                    }

                    .textarea {
                        min-height: 40px !important;
                        max-height: 100px !important;
                        font-size: 14px !important;
                        padding: 10px 12px !important;
                    }

                    .send-btn {
                        width: 40px !important;
                        height: 40px !important;
                        border-radius: var(--lc-radius-full) !important;
                    }

                    /* 移动端区域容器 */
                    .mobile-area {
                        min-height: 70px !important;
                    }

                    .pc-area {
                        min-height: 70px !important;
                    }
                }

                /* .know-widget 曾经写死 min-height: calc(100% - 60px) !important —— 那个
                   60 是很久以前 header 的高度。header 现在是 104/100/92（两行）或 56
                   （home 单行），这条规则于是把知识库内容区顶成 100%-60，比可用空间高
                   出 40+px，底部一直被裁掉（面板 overflow:hidden 所以看不出是溢出，
                   只觉得"知识库底部少了一截"）。
                   高度现在由面板的 flex 列布局决定（header 固定 + 内容区 flex:1），
                   不需要也不能再有第二个来源，故整条删除。 */

                /* 工具栏按钮样式 */
                .toolbar a {
                    display: flex !important;
                    align-items: center !important;
                    justify-content: center !important;
                    width: 32px !important;
                    height: 32px !important;
                    border-radius: var(--lc-radius-sm) !important;
                    transition: background-color var(--lc-dur-fast) var(--lc-ease-out), color var(--lc-dur-fast) var(--lc-ease-out) !important;
                    cursor: pointer !important;
                    background-color: transparent;
                    color: var(--lc-text-2, #6b7280) !important;   /* muted slate — icons no longer flat black */
                    text-decoration: none;
                    flex-shrink: 0 !important;
                }

                .toolbar a svg {
                    width: 20px !important;
                    height: 20px !important;
                    display: block !important;
                }

                /* hover: subtle brand-tinted pill + brand-colored icon */
                .toolbar a:hover {
                    background-color: rgba(var(--widget-main-rgb, 37, 99, 235), 0.1) !important;
                    color: var(--widget-main-color, #2563eb) !important;
                }

                .toolbar a:active {
                    background-color: rgba(var(--widget-main-rgb, 37, 99, 235), 0.18) !important;
                    color: var(--widget-main-color, #2563eb) !important;
                    transform: scale(0.94);
                }

                /* 文件上传样式 */
                .uploadimg {
                    display: flex !important;
                    align-items: center !important;
                    justify-content: center !important;
                    width: 36px !important;
                    height: 36px !important;
                    flex-shrink: 0 !important;
                }

                .uploadimg label {
                    display: flex !important;
                    align-items: center !important;
                    justify-content: center !important;
                    width: 100% !important;
                    height: 100% !important;
                    cursor: pointer !important;
                    border-radius: var(--lc-radius-lg) !important;
                    transition: background-color var(--lc-dur-base) var(--lc-ease-out) !important;
                }

                .uploadimg label:hover {
                    background-color: rgba(0, 0, 0, 0.05) !important;
                }

                .fileInput {
                    width: 0.1px;
                    height: 0.1px;
                    opacity: 0;
                    overflow: hidden;
                    position: absolute;
                    z-index: -1;
                }

                /* 颜色专项:原来是 bootstrap 蓝 #007BFF / hover #0056b3 —— 与出厂蓝
                   都不是同一个蓝,商户配任何主色它都纹丝不动。改跟主色;hover 走
                   同色系加深(brightness 滤镜,不需要再手算一个深色)。 */
                .fileInputLabel {
                    cursor: pointer;
                    line-height: 20px;
                    display: inline-block;
                    padding: 2px 2px;
                    font-size: 12px;
                    background-color: var(--lc-main);
                    color: var(--lc-bubble-user-text, #ffffff);
                    border-radius: var(--lc-radius-xs);
                    transition: filter var(--lc-dur-slow) var(--lc-ease-out);
                }

                .fileInputLabel:hover {
                    filter: brightness(0.9);
                }

                /* Emoji选择器样式。高度不写死：弹层铺满它所在的容器（chat-frame
                   已经把 .emoji-pick 的 inset 收到 composer 之上），内部由
                   em-emoji-picker 100% 继承 —— 旧的 350px 在 700/860 两档面板下
                   要么留一大片空白要么被截断。 */
                .emoji-pick {
                    position: absolute;
                    inset: 0;
                    z-index: 9999999;
                    display: flex;
                    flex-direction: column;
                    overflow: hidden;
                }

                /* emoji-mart 主题变量：跟随 widget 设计语言（系统字族 + 中性文字
                   + 主题色强调）。这里原先是一套调试期留下的 Comic Sans/紫粉配色。 */
                em-emoji-picker {
                    --border-radius: var(--lc-radius-md);
                    --category-icon-size: 20px;
                    --color-border-over: rgba(0, 0, 0, 0.1);
                    --color-border: rgba(0, 0, 0, 0.05);
                    --font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
                    --font-size: 14px;
                    --rgb-accent: var(--widget-main-rgb, 37, 99, 235);
                    --rgb-background: var(--lc-emoji-bg-rgb, 255, 255, 255);
                    --rgb-color: var(--lc-emoji-text-rgb, 30, 41, 59);
                    --rgb-input: var(--lc-emoji-input-rgb, 241, 245, 249);
                    --shadow: 0 8px 24px rgba(0, 0, 0, .12);
                }

                /* GIF选择器样式。
                   密度：弹层高度是固定的（面板高 − composer 高），所以每一像素外框
                   留白都直接换成少半行贴纸。实测 400×700 面板下，旧版 10px 内边距 +
                   8px 表单外距 + 41px 输入框，只给网格剩 395px = 6 行；且每行只排得下
                   4 张（4×68 + 3×10 = 302，右侧白扔 66px，第 5 张差 0.x px 排不进去）。
                   这里把外框留白压到 8px、表单外距 6px、输入框 39px，网格拿回 405px，
                   配合 6px 网格间距变成 7 行 × 5 列 —— 缩略图尺寸一格没动（64×52）。 */
                gif-picker {
                    background: var(--lc-surface, #fff);
                    position: absolute;
                    inset: 0;
                    display: flex;
                    flex-direction: column;
                    overflow: hidden;
                    padding: var(--lc-space-2);
                    box-sizing: border-box;
                }
                gif-picker form { flex-shrink: 0; margin: 0 0 6px 0; }
                gif-picker input {
                    width: 100%;
                    box-sizing: border-box;
                    padding: var(--lc-space-2) var(--lc-space-3);
                    border: var(--lc-hairline);
                    border-radius: var(--lc-radius-sm);
                    font-size: 14px;
                    outline: none;
                    background: var(--lc-surface-2, #f9fafb);
                    color: var(--lc-text, #111827);
                    transition: border-color var(--lc-dur-fast) var(--lc-ease-out), background var(--lc-dur-fast) var(--lc-ease-out);
                }
                gif-picker input::placeholder { color: var(--lc-text-3, #9ca3af); }
                gif-picker input:focus {
                    border-color: var(--widget-main-color, #2563eb);
                    background: var(--lc-surface, #fff);
                }
                gif-picker > div {
                    flex: 1 1 auto;
                    min-height: 0;
                    overflow-y: auto;
                    align-content: flex-start !important;
                    gap: var(--lc-space-2) !important;
                }

                /* 贴纸占位框：--cc-1f73p 是个从来没定义过的变量，background-color
                   直接失效、border-color 回落成 currentColor（黑框）。换成中性占位。
                   margin 归零：外边距叠在 flex gap 上等于双份间距，横向每张多占 4px，
                   正是第 5 列排不进去的那 4px。 */
                gif-picker .sticker{
                    background-color: var(--lc-surface-2, #f1f5f9) !important;
                    border: var(--lc-hairline) !important;
                    width: 64px !important;
                    height: 52px !important;
                    margin: 0 !important;
                    display: inline-block !important;
                    position: relative !important;
                    overflow: hidden !important;
                    border-radius: var(--lc-radius-sm) !important;
                }

                gif-picker .sticker-sub{
                    background-size: cover !important;
                    background-position: center !important;
                    background-repeat: no-repeat !important;
                    cursor: pointer !important;
                    position: absolute !important;
                    inset: 0 !important;
                    z-index: 2 !important;
                }
                /* 两个弹层都铺满各自的宿主容器（尺寸模型只由面板说了算） */
                em-emoji-picker, gif-picker{
                    position: absolute;
                    inset: 0;
                    width: 100%;
                    height: 100%;
                }
                /* 移动端特定样式 */
                @media only screen and (max-width: 500px) {
                    /* 移动端显示滚动按钮 */
                    .toolbar-scroll-btn {
                        display: flex !important;
                    }

                    .toolbar a {
                        width: 40px !important;
                        height: 40px !important;
                    }

                    .uploadimg {
                        width: 40px !important;
                        height: 40px !important;
                    }

                    .toolbar a::after, .uploadimg label::after {
                        content: '';
                        position: absolute;
                        top: -4px;
                        left: -4px;
                        right: -4px;
                        bottom: -4px;
                    }

                    em-emoji-picker, gif-picker{
                        position: absolute;
                        inset: 0;
                        width: 100%;
                        height: 100%;
                    }
                }

                /* 回复功能样式 */
                .reply-msg .msg {
                    padding-bottom: 10px;
                }

                .replied-message {
                    background-color: rgba(0, 0, 0, 0.06);
                    border-left: 3px solid var(--widget-main-color, #2563eb);
                    padding: var(--lc-space-2) var(--lc-space-3);
                    margin-bottom: var(--lc-space-2);
                    border-radius: 0 var(--lc-radius-sm) var(--lc-radius-sm) 0;
                    font-size: 0.85em;
                    cursor: pointer;
                    transition: background-color var(--lc-dur-fast);
                }

                .replied-message:hover {
                    background-color: rgba(0, 0, 0, 0.1);
                }

                /* visitor (蓝色气泡) 内的引用 */
                .visitor .replied-message {
                    background-color: rgba(255, 255, 255, 0.15);
                    border-left-color: rgba(255, 255, 255, 0.6);
                }
                .visitor .replied-message:hover {
                    background-color: rgba(255, 255, 255, 0.25);
                }
                .visitor .reply-header {
                    color: rgba(255, 255, 255, 0.7);
                }
                .visitor .reply-from {
                    color: rgba(255, 255, 255, 0.9);
                }
                .visitor .reply-content {
                    color: rgba(255, 255, 255, 0.8);
                }

                .reply-header {
                    display: flex;
                    align-items: center;
                    margin-bottom: 3px;
                    color: var(--lc-text-3, #888);
                    font-size: 0.85em;
                }

                .reply-icon {
                    margin-right: 5px;
                    flex-shrink: 0;
                }

                .reply-from {
                    font-weight: 600;
                    color: var(--lc-text-2, #555);
                }

                .reply-content {
                    color: var(--lc-text-2, #666);
                }

                .reply-content p {
                    margin: 0;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    display: -webkit-box;
                    -webkit-line-clamp: 2;
                    -webkit-box-orient: vertical;
                }

                /* 回复指示器 */
                .reply-indicator {
                    position: sticky;
                    top: 0;
                    background-color: var(--lc-surface-2, #f0f0f0);
                    border-bottom: var(--lc-hairline);
                    padding: 10px 15px;
                    z-index: 100;
                    animation: slideDown var(--lc-dur-slow) var(--lc-ease-out);
                }

                @keyframes slideDown {
                    from {
                        transform: translateY(-100%);
                        opacity: 0;
                    }
                    to {
                        transform: translateY(0);
                        opacity: 1;
                    }
                }

                .reply-bar {
                    bottom: 0;
                    left: 0;
                    right: 0;
                    background-color: var(--lc-surface-2, #f8f8f8);
                    border-bottom: var(--lc-hairline);
                    padding: var(--lc-space-2) var(--lc-space-3);
                    cursor: pointer;
                    transition: background-color var(--lc-dur-base) var(--lc-ease-out), bottom var(--lc-dur-slow) var(--lc-ease-out);
                    animation: slideUp var(--lc-dur-slow) var(--lc-ease-out);
                    z-index: 999;
                }

                @keyframes slideUp {
                    from {
                        transform: translateY(100%);
                        opacity: 0;
                    }
                    to {
                        transform: translateY(0);
                        opacity: 1;
                    }
                }

                .reply-bar:hover {
                    background-color: var(--lc-surface-3, #f0f0f0);
                }

                /* 进度条样式 */
                .progress-bar-container {
                    width: 100%;
                    background-color: var(--lc-surface-3, #ddd);
                    border-radius: var(--lc-radius-xs);
                    margin: 10px 0;
                    position: absolute;
                    left: 0;
                    bottom: 80px;
                }

                /* R8:进度跟主色,不跟"成功绿"。渐变见 --lc-main-gradient 注释。 */
                .progress-bar {
                    height: 20px;
                    background: var(--lc-main-gradient);
                    text-align: center;
                    line-height: 20px;
                    color: white;
                    width: 0%;
                    border-radius: var(--lc-radius-xs);
                    transition: width var(--lc-dur-slow) var(--lc-ease-out);
                }

                /* 删除按钮样式 */
                .delete-button {
                    padding: 0px;
                    background-color: #ff4d4d;
                    color: white;
                    border: none;
                    border-radius: var(--lc-radius-full);
                    cursor: pointer;
                    font-size: 16px;
                    line-height: 20px;
                    display: inline-block;
                    text-align: center;
                    width: 20px;
                    height: 20px;
                    top: 15px;
                    position: absolute;
                }

                .delete-button:hover {
                    background-color: #ff3333;
                }

                /* 图片和动画样式 */
                .chat-ani {
                    max-height: 120px;
                    max-width: 120px;
                }

                .chat-ani img {
                    display: block;
                    max-width: 100%;
                    height: auto;
                    background-color: var(--lc-surface-2, #f5f5f5);
                    min-height: 50px;
                }

                .chat-ani video {
                    display: block;
                    max-width: 100%;
                    height: auto;
                    background-color: var(--lc-surface-2, #f5f5f5);
                    min-height: 100px;
                }

                .chat-msg img{
                    max-width: 150px;
                    height: auto;
                }

                .sticker{
                    max-width: 100%;
                    height: auto;
                    background-color: transparent;
                }

                /* 隐藏video控件 */
                .sticker::-webkit-media-controls-enclosure {
                    display: none!important;
                }

                .sticker::-webkit-media-controls-panel{
                    display: none!important;
                }

                .sticker::-webkit-media-controls-fullscreen-button {
                   display: none;
                }

                .sticker::-webkit-media-controls-play-button {
                    display: none;
                }

                .sticker::-webkit-media-controls-timeline {
                    display: none;
                }

                .sticker::-webkit-media-controls-current-time-display{
                    display: none;
                }

                .sticker::-webkit-media-controls-time-remaining-display {
                    display: none;
                }

                .sticker::-webkit-media-controls-mute-button {
                    display: none;
                }

                .sticker::-webkit-media-controls-toggle-closed-captions-button {
                    display: none;
                }

                .sticker::-webkit-media-controls-volume-slider {
                    display: none;
                }

                /* PC端区域样式 —— 正常流(flex 列底部项)，不再 absolute 浮在消息上 */
                .pc-area{
                    position: relative;
                    z-index: 1000;
                    background: transparent;
                    flex-shrink: 0;
                }

                /* 移动端区域样式 */
                .mobile-area{
                    position: relative;
                    z-index: 1000;
                    background: transparent;
                    flex-shrink: 0;
                }

                .mobile-area .reply-bar {
                    position: relative;
                    z-index: 999;
                    bottom: auto;
                    transform: none;
                    margin-bottom: 0;
                }

                /* 滚动条样式 - 已在下方使用现代风格 */

                /* 高亮和动画效果 */
                .highlight-message {
                    animation: highlightPulse 2s var(--lc-ease-out);
                }

                @keyframes highlightPulse {
                    0% {
                        background-color: rgba(25, 114, 245, 0.2);
                    }
                    50% {
                        background-color: rgba(25, 114, 245, 0.1);
                    }
                    100% {
                        background-color: transparent;
                    }
                }

                .chat-container.new-message-highlight {
                    animation: highlight-flash 1s var(--lc-ease-out);
                }

                @keyframes highlight-flash {
                    0% {
                        box-shadow: 0 0 0 0 rgba(33, 150, 243, 0);
                    }
                    50% {
                        box-shadow: inset 0 0 20px 5px rgba(33, 150, 243, 0.3);
                    }
                    100% {
                        box-shadow: 0 0 0 0 rgba(33, 150, 243, 0);
                    }
                }

                /* 加载动画 */
                .loading-dots {
                    display: inline-block;
                }

                .loading-dots span {
                    display: inline-block;
                    animation: loading-dots 1.4s infinite var(--lc-ease-in-out) both;
                }

                .loading-dots span:nth-child(1) {
                    animation-delay: -0.32s;
                }

                .loading-dots span:nth-child(2) {
                    animation-delay: -0.16s;
                }

                @keyframes loading-dots {
                    0%, 80%, 100% {
                        opacity: 0.3;
                    }
                    40% {
                        opacity: 1;
                    }
                }

                /* 新消息气泡 */
                /* 颜色专项:前景原为写死 white —— 商户配浅色主色(黄/浅绿)时白字
                   直接看不见。--lc-bubble-user-text 是 pickTextOn 按对比度选出来的
                   "主色上的前景",与访客气泡同一套判据。 */
                .new-message-bubble {
                    position: absolute;
                    bottom: 20px;
                    left: 50%;
                    transform: translateX(-50%);
                    background: var(--widget-main-color, #2563eb);
                    color: var(--lc-bubble-user-text, #ffffff);
                    border-radius: var(--lc-radius-full);
                    padding: var(--lc-space-2) var(--lc-space-4);
                    box-shadow: var(--lc-shadow-2);
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    gap: var(--lc-space-2);
                    transition: all var(--lc-dur-base) var(--lc-ease-out);
                    z-index: 100;
                    animation: bounceIn var(--lc-dur-slow) var(--lc-ease-out);
                }

                @keyframes bounceIn {
                    0% {
                        transform: translateX(-50%) scale(0);
                        opacity: 0;
                    }
                    50% {
                        transform: translateX(-50%) scale(1.1);
                    }
                    100% {
                        transform: translateX(-50%) scale(1);
                        opacity: 1;
                    }
                }

                .new-message-bubble:hover {
                    /* 颜色专项:原为写死 #1565D8(某年的深蓝),主色换成橙之后
                       hover 一下就跳回蓝色。同色系加深,不引入第二个色相。 */
                    filter: brightness(0.9);
                    transform: translateX(-50%) translateY(-2px);
                    box-shadow: var(--lc-shadow-2);
                }

                .system .msg {
                    background: unset;
                    max-width: 100%;
                    padding: 0;
                }

                /* System message centered style */
                .system {
                    justify-content: center !important;
                }

                .system .msg {
                    background: var(--lc-system-msg-bg, rgba(0, 0, 0, 0.04)) !important;
                    color: var(--lc-text-2, #64748b) !important;
                    font-size: 12px !important;
                    padding: var(--lc-space-1) var(--lc-space-3) !important;
                    border-radius: var(--lc-radius-md) !important;
                    text-align: center !important;
                    max-width: 80% !important;
                    box-shadow: none !important;
                }

                /* Typing indicator */
                .typing-indicator {
                    display: flex;
                    align-items: center;
                    gap: var(--lc-space-1);
                    padding: var(--lc-space-3) var(--lc-space-4);
                }

                .typing-indicator .dot {
                    width: 7px;
                    height: 7px;
                    background: var(--lc-text-3, #94a3b8);
                    border-radius: var(--lc-radius-full);
                    animation: typingBounce 1.4s var(--lc-ease-in-out) infinite;
                }

                .typing-indicator .dot:nth-child(2) {
                    animation-delay: 0.16s;
                }

                .typing-indicator .dot:nth-child(3) {
                    animation-delay: 0.32s;
                }

                @keyframes typingBounce {
                    0%, 60%, 100% { transform: translateY(0); }
                    30% { transform: translateY(-6px); }
                }

                /* Message slide-in animation */
                .message-enter {
                    animation: messageSlideIn var(--lc-dur-slow) var(--lc-ease-out);
                }

                @keyframes messageSlideIn {
                    from {
                        opacity: 0;
                        transform: translateY(12px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                /* 面板开合动画 —— 桌面从浮球角落 scale+fade，手机从底部上滑+fade。
                   transform/opacity only；JS 侧（widget.js）在 prefers-reduced-motion
                   时不挂这些 animation。 */
                @keyframes lcPanelIn {
                    from { opacity: 0; transform: scale(0.95) translateY(8px); }
                    to   { opacity: 1; transform: scale(1) translateY(0); }
                }
                @keyframes lcPanelInMobile {
                    from { opacity: 0; transform: translateY(18px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
                /* 关闭出场：反向收回浮球角落，动画结束后 JS 才真正卸载（forwards 保持终态防闪回） */
                @keyframes lcPanelOut {
                    from { opacity: 1; transform: scale(1) translateY(0); }
                    to   { opacity: 0; transform: scale(0.95) translateY(8px); }
                }
                @keyframes lcPanelOutMobile {
                    from { opacity: 1; transform: translateY(0); }
                    to   { opacity: 0; transform: translateY(18px); }
                }

                /* 视图切换（home/chat/help）方向感知 push/pop。
                   A/B 两份同形 keyframes：同一元素连续两次同方向切换时交替使用，
                   否则 inline style 不变不会重播动画。 */
                @keyframes lcViewPushA {
                    from { opacity: 0; transform: translateX(24px); }
                    to   { opacity: 1; transform: translateX(0); }
                }
                @keyframes lcViewPushB {
                    from { opacity: 0; transform: translateX(24px); }
                    to   { opacity: 1; transform: translateX(0); }
                }
                @keyframes lcViewPopA {
                    from { opacity: 0; transform: translateX(-24px); }
                    to   { opacity: 1; transform: translateX(0); }
                }
                @keyframes lcViewPopB {
                    from { opacity: 0; transform: translateX(-24px); }
                    to   { opacity: 1; transform: translateX(0); }
                }
                /* 兜底：系统开了减弱动效时，即便 JS 判定失效也硬关这些动画 */
                @media (prefers-reduced-motion: reduce) {
                    [style*="lcPanelIn"], [style*="lcPanelOut"],
                    [style*="lcViewPush"], [style*="lcViewPop"] {
                        animation: none !important;
                    }
                    .lc-tabs-indicator { transition: none !important; }
                    .lc-skeleton-block { animation: none !important; opacity: 0.65 !important; }
                }

                /* 首屏骨架（widget.js renderSkeleton）：/v1/chat 还没返回时占位，避免
                   在拿到 dash 配的主色之前就先渲出一版蓝色系统色的"假"UI——那样等
                   真色到达再切换反而更显眼。纯中性灰，不含任何主题色，谁的品牌色
                   都不冲突。*/
                @keyframes lcSkeletonPulse {
                    0%, 100% { opacity: 0.55; }
                    50%      { opacity: 1; }
                }
                .lc-skeleton-block {
                    background: var(--lc-surface-3, #e2e8f0);
                    border-radius: var(--lc-radius-sm);
                    animation: lcSkeletonPulse 1.4s var(--lc-ease-in-out) infinite;
                }

                /* underline 档 tab：非活动项 hover 浅底反馈 */
                .lc-tab-underline { transition: background var(--lc-dur-fast) var(--lc-ease-out), opacity var(--lc-dur-fast) var(--lc-ease-out); }
                .lc-tab-underline:hover { background: rgba(255, 255, 255, 0.12) !important; }

                /* 聊天空态建议 pill：浅灰底，hover 主色描边 */
                .lc-suggest-pill {
                    transition: border-color var(--lc-dur-fast) var(--lc-ease-out), background var(--lc-dur-fast) var(--lc-ease-out), color var(--lc-dur-fast) var(--lc-ease-out);
                }
                .lc-suggest-pill:hover {
                    border-color: var(--widget-main-color, #2563eb) !important;
                    background: var(--lc-surface, #ffffff) !important;
                    color: var(--widget-main-color, #2563eb) !important;
                }

                /* 空态 / 骨架 */
                /* 此处原有第二份同名脉冲 keyframes（.5→1），后定义胜出，把上面
                   契约值 0.55 悄悄改成了 .5；已删，全站共用上面那份。
                   本段 CSS 在 JS 模板串里：注释禁用反引号，且会原样下发。 */
                /* R11:呼吸周期 1400ms。这两条原本是 1.3s —— 同一张脸上另外三处
                   骨架(boot 骨架/首屏骨架/embed 骨架)都是 1.4s,它俩慢半拍地各
                   呼吸各的,同屏时能看出两组块不同步。曲线走 R14 的循环档。 */
                .lc-skel-avatar {
                    width: 32px; height: 32px; border-radius: var(--lc-radius-full);
                    background: var(--lc-surface-3, #e5e9ef); flex-shrink: 0;
                    animation: lcSkeletonPulse 1.4s var(--lc-ease-in-out) infinite;
                }
                .lc-skel-bubble {
                    background: var(--lc-surface-3, #e5e9ef); border-radius: var(--lc-radius-md);
                    animation: lcSkeletonPulse 1.4s var(--lc-ease-in-out) infinite;
                }
                .lc-empty-state {
                    animation: messageSlideIn var(--lc-dur-slow) var(--lc-ease-out);
                }

                /* Chat closed overlay */
                .chat-closed-banner {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: var(--lc-space-3) var(--lc-space-4);
                    background: var(--lc-bg, #f8fafc);
                    color: var(--lc-text-2, #64748b);
                    font-size: 13px;
                    border-top: var(--lc-hairline);
                    gap: var(--lc-space-2);
                }

                .chat-closed-banner button {
                    background: var(--widget-main-color, #2563eb);
                    /* 颜色专项:同 .new-message-bubble —— 主色上的前景走 pickTextOn。 */
                    color: var(--lc-bubble-user-text, #ffffff);
                    border: none;
                    padding: 6px 14px;
                    border-radius: var(--lc-radius-sm);
                    font-size: 13px;
                    cursor: pointer;
                    font-weight: 500;
                    transition: background var(--lc-dur-base);
                }

                .chat-closed-banner button:hover {
                    filter: brightness(0.9);
                }

                /* Scrollbar modern style */
                ::-webkit-scrollbar {
                    width: 6px;
                    height: 6px;
                }

                ::-webkit-scrollbar-track {
                    background: transparent;
                }

                ::-webkit-scrollbar-thumb {
                    background: var(--lc-scrollbar, rgba(0, 0, 0, 0.15));
                    border-radius: var(--lc-radius-xs);
                    border: none;
                    box-shadow: none;
                }

                ::-webkit-scrollbar-thumb:hover {
                    background: var(--lc-scrollbar-hover, rgba(0, 0, 0, 0.25));
                    box-shadow: none;
                }

                /* 知识库分类条：横向可滑但不显滚动条。上面的通用 6px 规则会在这条
                   胶囊行下面画一条横杠，还会把这行撑高 6px —— 顶部固定条的高度
                   必须只由内容决定。 */
                .lc-cat-scroll::-webkit-scrollbar {
                    width: 0;
                    height: 0;
                    display: none;
                }

                /* Reply bar modern style */
                .reply-bar {
                    border-left: 3px solid var(--widget-main-color, #2563eb) !important;
                    background-color: var(--lc-bg, #f8fafc) !important;
                }

                .replied-message {
                    border-left-color: var(--widget-main-color, #2563eb) !important;
                }
            `;
            shadowRoot.appendChild(shadowStyles);

            // 创建Widget容器
            const widgetContainer = document.createElement('div');
            widgetContainer.className = 'widget-root';
            // Inline 模式：widget-root 是 shadow 内的挂载点，默认高度=内容(≈520 兜底)，
            // 在 flex 容器里 flex:0 不 grow → 高容器里撑不满、输入框悬空。
            // 让它 height:100% + flex column：容器有高度就填满，无高度回退 inlineMinHeight。
            //
            // 但这个 minHeight 是 CSS flexbox 的硬地板——flex-shrink 越不过去。widget.html
            // （fill 和 centered 都一样）自己的 CSS 链（html/body/#chatly-container 全是
            // 明确的 height:100%/dvh）永远给我们真实高度，#chatly-container 又
            // overflow:hidden；键盘弹起时可视高度可能远小于 520px（真机反馈：Android
            // WebView fill 布局下键盘一弹，输入框/"Powered by" 被裁掉半截——键盘高度本身
            // 因设备/输入法而异，不是个固定阈值，不能靠"再加一个更大的兜底数字"绕过），
            // 520 的地板会顶破这条真实链路，把面板撑高到 520px，溢出部分被
            // #chatly-container 裁掉。只有"宿主环境没有 widget.html 这层、我们没法确定
            // 对方给没给我们高度"（第三方直接把 JS SDK inline 模式挂进自己页面的任意 div）
            // 时才需要这个兜底防止零高度塌陷——#chatly-container 是 widget.html 独有的
            // 挂载点 id，用它的存在与否精确区分这两种宿主（同一判据见 widget.js 的
            // _inlineMinHeightFallback，两处必须一致）。
            if (window.laneChatMode === 'inline') {
                widgetContainer.style.height = '100%';
                widgetContainer.style.display = 'flex';
                widgetContainer.style.flexDirection = 'column';
                const inlineMinHeight = resolveInlineMinHeightFallback({
                    hasReliableHeightChain: typeof document !== 'undefined' && !!document.getElementById('chatly-container'),
                    configuredMinHeight: window.laneChatCustomizations && window.laneChatCustomizations.inlineMinHeight,
                });
                if (inlineMinHeight !== undefined) {
                    widgetContainer.style.minHeight = inlineMinHeight;
                }
            }
            shadowRoot.appendChild(widgetContainer);

            renderContainer = widgetContainer;
            debugLog('[Widget] Using Shadow DOM isolation');
        } else {
            // 降级到CSS前缀方案
            // 不设置任何样式，让 Widget 内部组件自己定位
            renderContainer = domNode;
            debugLog('[Widget] Using CSS fallback isolation');
        }

        const host = window.location.host || 'unknown-host';
        const conf = {
            ...defaultConfiguration,
            ...window.laneChatCustomizations,
            app_id: window.laneChatAppId,  // 添加app_id到配置中
            // window.laneChatMode 优先级高于 customizations.mode，保证 data-mode / JS 设置一致生效
            ...(window.laneChatMode ? { mode: window.laneChatMode } : {}),
            // widget.html 的 fill/centered 判定（同一处 boot script 里算好，见其
            // window.laneChatLayout 赋值）——面板自身的外沿圆角要跟着这个走：fill
            // 贴视口边缘该是直角，centered 悬浮卡片该保留圆角。只有 laneChatMode
            // ==='inline' 时才有意义（浮动 JS-SDK 面板不会设这个 window 变量，
            // conf.layout 缺省不影响它）。
            ...(window.laneChatLayout ? { layout: window.laneChatLayout } : {})
        };
        const from_data = window.from_data ?? {};
        if (!from_data.url || typeof from_data.url == 'undefined') {
            from_data.url = window.location.href
        }

        // 创建 emoji picker portal 容器
        // 如果使用 Shadow DOM，添加到 Shadow DOM 内部（但不在 React 容器内）
        // 否则添加到 domNode
        const emojiPortalContainer = document.createElement('div');
        emojiPortalContainer.id = 'laneChatEmojiPortal';

        // 添加样式
        const portalStyle = document.createElement('style');
        portalStyle.textContent = `
            #laneChatEmojiPortal {
                /* 容器不占据空间 */
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                pointer-events: none; /* 容器本身不阻挡事件 */
                z-index: 9999999;
            }

            #laneChatEmojiPortal .emoji-pick {
                position: absolute;
                inset: 0;
                z-index: 9999999;
                display: flex;
                flex-direction: column;
                overflow: hidden;
                background: var(--lc-surface, #fff) !important;
                border-radius: var(--lc-radius-md);
                box-shadow: var(--lc-shadow-3);
                pointer-events: all !important; /* emoji-pick 可以交互 */
            }

            #laneChatEmojiPortal em-emoji-picker {
                --border-radius: var(--lc-radius-md);
                --category-icon-size: 20px;
                --color-border-over: rgba(0, 0, 0, 0.1);
                --color-border: rgba(0, 0, 0, 0.05);
                --font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
                --font-size: 14px;
                --rgb-accent: var(--widget-main-rgb, 37, 99, 235);
                --rgb-background: var(--lc-emoji-bg-rgb, 255, 255, 255);
                --rgb-color: var(--lc-emoji-text-rgb, 30, 41, 59);
                --rgb-input: var(--lc-emoji-input-rgb, 241, 245, 249);
                --shadow: 0 8px 24px rgba(0, 0, 0, .12);
                position: absolute;
                inset: 0;
                width: 100%;
                height: 100%;
            }
        `;
        emojiPortalContainer.appendChild(portalStyle);

        // 添加到 Shadow DOM 内部（如果支持）或 domNode
        if (supportsShadowDOM) {
            shadowRoot.appendChild(emojiPortalContainer);
            debugLog('[Widget] Emoji portal added to Shadow DOM');
        } else {
            domNode.appendChild(emojiPortalContainer);
            debugLog('[Widget] Emoji portal added to regular DOM');
        }

        // 注入主题CSS变量到Shadow DOM。
        // 这里原本有一份自己手写的 hexToRgb（slice(1,3)/slice(3,5)/slice(5,7) 硬切），
        // 对 `rgba(37, 99, 235, .5)` 会切出 "gb"/"a("/"37" → `NaN, NaN, 55`。要命的
        // 不是难看：自定义属性在解析期接受任意 token，所以变量**确实被定义了**，
        // 满 widget 的 `rgba(var(--widget-main-rgb, 37, 99, 235), .1)` fallback 因此
        // 不会生效，那些声明在计算期变成非法、品牌色 hover/焦点环整片消失。
        // 换成 theme-color.js 的 colorToRgbTriplet（认所有颜色形态，认不出回 null，
        // 那时干脆不写这个变量，让 CSS 侧的 fallback 正常接管）。
        if (supportsShadowDOM && shadowRoot) {
            const themeVarsStyle = document.createElement('style');
            const mc = conf.mainColor || '#2563eb';
            const mcRgb = colorToRgbTriplet(mc);
            themeVarsStyle.textContent = `
                :host {
                    --widget-main-color: ${mc};${mcRgb ? `
                    --widget-main-rgb: ${mcRgb};` : ''}
                }
            `;
            shadowRoot.appendChild(themeVarsStyle);
        }

        // 在适当的容器中创建React根
        const root = createRoot(renderContainer);

        // Widget组件会在componentDidMount中自动设置引用

        root.render(
            <Widget
                paymentCatAppId={window.laneChatAppId}
                host={host}
                isMobile={window.screen.width < 500}
                conf={conf}
                fromData={from_data}
                emojiPortalContainer={emojiPortalContainer}
                // 首屏之后 mainColor 可能会变(/v1/chat 早期上色 / usage/start 完整
                // applyConfig)，--widget-main-color/--widget-main-rgb 这两个 CSS
                // 变量却是当年一次性 :host{} <style> 注入的，不会跟着更新——发送键/
                // focus 环这些靠 var() 读色的元素因此会卡在首屏那一份色（customizations
                // 或出厂默认）。把宿主元素传给 Widget，让它在 mainColor 变化时用内联
                // style.setProperty 动态改写（内联样式能改，<style> 文本节点不方便
                // 频繁重写）。domNode 在 Shadow DOM / CSS 前缀两条路径下都是同一个
                // 元素（attachShadow 的宿主 / renderContainer 本身），两条路径都适用。
                shadowHostEl={domNode}
                // 直达聊天入口（widget.html ?view=chat&chat_id=<N>）：目标会话 id，
                // 校验交给 parsePositiveIntId（theme-color.js 纯函数）；缺省/非法
                // 时是 null，Widget/ChatFrame/Chat 全链路 no-op，回落既有行为
                // （服务端默认挑的最新/新建会话）。
                initialChatId={parsePositiveIntId(window.laneChatInitialChatId)}
                // widget.html ?view=chat 本身（不一定带 chat_id）：显式选了"落地
                // 聊天视图"，必须盖过首次访问的 Home 默认落地
                // （resolveFirstVisitLandingView 的 hasExplicitChatTarget）。之前
                // window.laneChatInitialView 只在 widget.html 里被设置、这里从没
                // 读过（"view=chat 本身不改变任何渲染路径"——那是首次访问落地
                // 视图这个功能存在之前的说法，现在它改变了）。
                forceChatView={window.laneChatInitialView === 'chat'}
            />);

        // 使用统计已移到 Widget 组件的 loadAppConfig() 方法中
        // Widget componentDidMount 会自动调用 /api/usage/start 并应用配置

        // 保存 root 实例以便销毁
        window.laneChatSDK._reactRoot = root;
        // 标记已挂载（多实例防重复的第一道防线）
        window.__laneChatMounted = true;
    }
}

// 销毁函数 - 用于清理 Widget
//
// 调用时机：
//   - SPA 路由切换前（destroy 完再 init 新实例）
//   - 用户主动隐藏 widget（需要时再 init 恢复）
//   - 页面卸载前的资源回收
//
// 会做的事：
//   1. 关闭打开中的聊天窗口（触发 componentWillUnmount 内的 listener 移除）
//   2. root.unmount() 卸载 React 树 —— React 18 的正确方式
//   3. 从 DOM 中移除 #laneChatRoot（连带 Shadow DOM / emoji portal 一起删）
//   4. 清理 __laneChatMounted 标志，释放后续 init() 机会
//   5. 重置 _widgetInstance / _reactRoot 引用避免泄漏
function destroyChat() {
    try { window.__laneChatDestroyCount = (window.__laneChatDestroyCount || 0) + 1; } catch (_) {}
    try { window.__laneChatLastDestroyAt = Date.now(); } catch (_) {}
    try {
        window.__laneChatDestroyStacks = window.__laneChatDestroyStacks || [];
        window.__laneChatDestroyStacks.push(new Error().stack);
    } catch (_) {}

    // Inject 在途中：不真卸载，等 inject 收尾时 injectChat 的 finally 会补做一次 destroy。
    // 紧接着若有 init() 抢在 finally 前触发，会把这个 pending flag 清掉 —— 实现 destroy+init 合并。
    if (window.__laneChatInjectInProgress === true) {
        window.__laneChatPendingDestroy = true;
        return;
    }

    // 已挂载 + 有实例：延迟 150ms 真卸载。窗口内 init() 会撤销这个 timer 并把
    // window.from_data 通过 setFromData 就地更新。宿主常见的 destroy+init 模式就被合并成
    // 一次 setFromData —— widget 不 flash，访客观感平滑。
    if (window.__laneChatMounted === true
        && window.laneChatSDK
        && window.laneChatSDK._widgetInstance
        && !window.__laneChatPendingDestroyTimer) {
        try { window.__laneChatDeferDestroyCount = (window.__laneChatDeferDestroyCount || 0) + 1; } catch (_) {}
        window.__laneChatPendingDestroyTimer = setTimeout(function () {
            try { window.__laneChatTimerFiredCount = (window.__laneChatTimerFiredCount || 0) + 1; } catch (_) {}
            window.__laneChatPendingDestroyTimer = null;
            _performDestroy();
        }, 150);
        return;
    }

    _performDestroy();
}

function _clearStoredVisitor(appId) {
    if (!appId) return;
    ['lane_uuid_', 'lane_fp_', 'lane_chatid_'].forEach(function(prefix) {
        try { localStorage.removeItem(prefix + appId); } catch (_) {}
    });
    ['lane_teaser_done_', 'lane_auto_opened_'].forEach(function(prefix) {
        try { sessionStorage.removeItem(prefix + appId); } catch (_) {}
    });
    try { document.cookie = 'lane_vt_' + appId + '=;path=/;max-age=0'; } catch (_) {}
}

function _performDestroy() {
    debugLog('[Lane.Chat] Destroying...');

    // 1. 关闭聊天窗口（Widget 组件的 componentWillUnmount 会自动移除 message listener）
    if (window.laneChatSDK && window.laneChatSDK._widgetInstance) {
        try {
            window.laneChatSDK._widgetInstance.closeChat();
        } catch (e) {
            console.warn('[Lane.Chat] Error closing chat:', e);
        }
    }

    // 2. 卸载 React 组件（React 18 使用 root.unmount()；这一步触发组件的 componentWillUnmount，
    //    Widget.componentWillUnmount 内部会调用 window.removeEventListener('message', handleMessage)）
    if (window.laneChatSDK && window.laneChatSDK._reactRoot) {
        try {
            debugLog('[Lane.Chat] Unmounting React root...');
            window.laneChatSDK._reactRoot.unmount();
        } catch (e) {
            console.warn('[Lane.Chat] Error unmounting React:', e);
        }
    }

    // 3. 移除 DOM 元素（laneChatRoot 包含了 emoji portal / Shadow DOM，一起删除）
    const widgetRoot = document.getElementById('laneChatRoot');
    if (widgetRoot) {
        debugLog('[Lane.Chat] Removing laneChatRoot element (includes emoji portal)...');
        if (widgetRoot.parentNode) {
            widgetRoot.parentNode.removeChild(widgetRoot);
        } else {
            widgetRoot.remove();
        }
    }

    // 4. 清理 Shadow DOM 的 fallback 样式
    const fallbackStyle = document.querySelector('style[data-widget-fallback]');
    if (fallbackStyle) {
        fallbackStyle.remove();
    }

    // 5. 重置 laneChatSDK 内部状态 + 清挂载标志
    if (window.laneChatSDK) {
        window.laneChatSDK._widgetInstance = null;
        window.laneChatSDK._reactRoot = null;
    }
    window.__laneChatMounted = false;

    debugLog('[Lane.Chat] Destroyed successfully');
}

// 暴露全局函数
window.injectChat = injectChat;
window.destroyChat = destroyChat;
